import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { announcementsAPI, devotionalAPI, messagesAPI, prayersAPI, benevolentAPI, notificationsAPI, authAPI, celebrationsAPI, dmAPI, inspirationalAPI, bibleAPI, livestreamAPI } from '../services/api';
import { churchInfo, prayerWallScripture } from '../data/mock';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import notificationSound from '../utils/notificationSound';
import { usePushNotifications } from '../hooks/usePushNotifications';
import WhatsNewPopup from '../components/WhatsNewPopup';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  BookOpen,
  MapPin,
  Bell,
  Youtube,
  Loader2,
  MoreVertical,
  Archive,
  ChevronRight,
  X,
  ZoomIn,
  Phone,
  MessageSquare,
  Globe,
  User,
  Send,
  Heart,
  Trash2,
  Video,
  Calendar,
  Copy,
  Check,
  Facebook,
  Volume2,
  BellRing,
  SmilePlus,
  Cake,
  Gift,
  PartyPopper,
  Share,
  PlusSquare,
  Smartphone
} from 'lucide-react';
import BottomNav from '../components/BottomNav';
import { useNavigate } from 'react-router-dom';
import { LinkifyText } from '../utils/linkify';

// Available reaction emojis
const REACTION_EMOJIS = ['👍', '❤️', '🙏', '😂', '🎉', '👏'];

// Notification/device helpers
const isIOSDevice = () =>
  typeof navigator !== 'undefined' &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

const isStandalonePWA = () =>
  typeof window !== 'undefined' &&
  (window.navigator.standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches);


const HomePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [devotional, setDevotional] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showPastorMessageDialog, setShowPastorMessageDialog] = useState(false);
  const [pastorMessage, setPastorMessage] = useState({ subject: '', content: '' });
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageSent, setMessageSent] = useState(false);
  const [newPrayerCount, setNewPrayerCount] = useState(0);
  const [pendingBenevolentCount, setPendingBenevolentCount] = useState(0);
  const [myBenevolentUpdatesCount, setMyBenevolentUpdatesCount] = useState(0);
  const [newInspirationalCount, setNewInspirationalCount] = useState(0);
  const [bibleHasNew, setBibleHasNew] = useState(false);
  const [isLive, setIsLive] = useState(false);
  const [newAnnouncementCount, setNewAnnouncementCount] = useState(0);
  const [showWatchDialog, setShowWatchDialog] = useState(false);
  const [showNotificationBanner, setShowNotificationBanner] = useState(false);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [showPushPrompt, setShowPushPrompt] = useState(false);
  const [pushPromptLoading, setPushPromptLoading] = useState(false);
  
  // Announcement comments and reactions
  const [announcementCommentText, setAnnouncementCommentText] = useState({});
  const [expandedAnnouncementComments, setExpandedAnnouncementComments] = useState({});
  const [reactionDetails, setReactionDetails] = useState({});
  const [loadingReactionDetails, setLoadingReactionDetails] = useState({});
  
  // Phone + PIN setup prompt
  const [showPhoneSetupPrompt, setShowPhoneSetupPrompt] = useState(false);
  const [phoneSetupData, setPhoneSetupData] = useState({ phone: '', pin: '', confirmPin: '' });
  const [phoneSetupLoading, setPhoneSetupLoading] = useState(false);
  const [phoneSetupError, setPhoneSetupError] = useState('');

  // Safety: keep the app interactive after any modal closes (guards against a
  // Radix issue that can leave pointer-events:none on <body> and "freeze" the app)
  useEffect(() => {
    const anyModalOpen = !!selectedImage || showPastorMessageDialog || showWatchDialog || showPushPrompt || showPhoneSetupPrompt;
    if (!anyModalOpen) {
      const t = setTimeout(() => { document.body.style.pointerEvents = ''; }, 150);
      return () => clearTimeout(t);
    }
  }, [selectedImage, showPastorMessageDialog, showWatchDialog, showPushPrompt, showPhoneSetupPrompt]);

  
  // Today's Celebrations
  const [celebrations, setCelebrations] = useState({ birthdays: [], anniversaries: [], hasCelebrations: false });
  const [sendingCelebrationMessage, setSendingCelebrationMessage] = useState(null);
  const [celebrationWishes, setCelebrationWishes] = useState({});
  const [wishInputs, setWishInputs] = useState({});
  const [expandedWishes, setExpandedWishes] = useState({});
  const [loadingWishes, setLoadingWishes] = useState({});
  
  // Push notifications hook
  const {
    isSupported: pushSupported,
    permission: pushPermission,
    isSubscribed: pushSubscribed,
    requestPermission,
    subscribe: subscribeToPush
  } = usePushNotifications();
  
  // Refs to track previous counts for sound triggers
  const prevPrayerCount = React.useRef(0);
  const prevBenevolentUpdates = React.useRef(0);
  const prevAnnouncementCount = React.useRef(0);
  const isFirstLoad = React.useRef(true);

  // Check if user needs to set up phone + PIN
  useEffect(() => {
    const checkPhoneSetup = async () => {
      // Don't show if already dismissed recently (within 7 days)
      const dismissed = localStorage.getItem('phoneSetupDismissed');
      if (dismissed) {
        const daysSinceDismissed = (Date.now() - new Date(dismissed).getTime()) / (1000 * 60 * 60 * 24);
        if (daysSinceDismissed < 7) return;
      }
      
      // Check if already completed
      const completed = localStorage.getItem('phoneSetupCompleted');
      if (completed) return;
      
      try {
        const status = await authAPI.getLoginStatus();
        if (!status.phoneLoginReady) {
          // Pre-fill phone if user has one
          if (status.phone) {
            setPhoneSetupData(prev => ({ ...prev, phone: status.phone }));
          }
          // Delay prompt slightly so it doesn't appear immediately
          setTimeout(() => {
            setShowPhoneSetupPrompt(true);
          }, 3000);
        }
      } catch (error) {
        console.error('Error checking phone setup status:', error);
      }
    };
    
    checkPhoneSetup();
  }, []);

  // Check if we should prompt for push notifications
  // Shows EVERY SESSION until user enables notifications (more persistent)
  useEffect(() => {
    const checkPushPrompt = () => {
      // Only prompt if:
      // 1. Push is supported
      // 2. Permission hasn't been denied
      // 3. User isn't already subscribed
      const accepted = localStorage.getItem('pushNotificationsEnabled');
      const sessionDismissed = sessionStorage.getItem('pushPromptDismissedThisSession');
      
      // Don't show if already accepted, already subscribed, or permission granted
      if (accepted || pushSubscribed || pushPermission === 'granted') {
        return;
      }
      
      // Don't show if already dismissed this session (user can dismiss once per visit)
      if (sessionDismissed) {
        return;
      }
      
      if (
        pushSupported &&
        pushPermission !== 'denied'
      ) {
        // Delay the prompt slightly so it doesn't appear immediately
        setTimeout(() => {
          setShowPushPrompt(true);
        }, 2000);
      } else if (isIOSDevice() && !isStandalonePWA()) {
        // iPhone users in Safari must install to Home Screen before push works.
        // Show the prompt in "install instructions" mode.
        setTimeout(() => {
          setShowPushPrompt(true);
        }, 2000);
      }
    };
    
    checkPushPrompt();
  }, [pushSupported, pushPermission, pushSubscribed]);

  const handleEnablePush = async () => {
    setPushPromptLoading(true);
    try {
      const granted = await requestPermission();
      if (granted) {
        await subscribeToPush();
        // Save that user has enabled notifications - won't prompt again
        localStorage.setItem('pushNotificationsEnabled', 'true');
      }
    } catch (error) {
      console.error('Error enabling push:', error);
    } finally {
      setPushPromptLoading(false);
      setShowPushPrompt(false);
    }
  };

  const handleDismissPushPrompt = () => {
    // Only dismiss for this session - will prompt again next time they open the app
    sessionStorage.setItem('pushPromptDismissedThisSession', 'true');
    setShowPushPrompt(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [annData, devData, celebrationData] = await Promise.all([
          announcementsAPI.getAll(),
          devotionalAPI.get(),
          celebrationsAPI.getToday()
        ]);
        setAnnouncements(annData);
        setDevotional(devData);
        setCelebrations(celebrationData);
      } catch (error) {
        console.error('Error fetching home data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    loadNewPrayerCount();
    loadPendingBenevolentCount();
    loadMyBenevolentUpdatesCount();
    loadNewAnnouncementCount();
    loadNewInspirationalCount();
    loadBibleDailyStatus();
    loadLivestreamStatus();
    checkForUnreadNotifications();
    
    // Set up polling for updates (every 30 seconds)
    const pollInterval = setInterval(() => {
      loadNewPrayerCount();
      loadMyBenevolentUpdatesCount();
      loadNewAnnouncementCount();
      loadNewInspirationalCount();
      loadLivestreamStatus();
    }, 30000);
    
    return () => clearInterval(pollInterval);
  }, []);

  // Check for unread notifications and show banner if any
  const checkForUnreadNotifications = async () => {
    try {
      const data = await notificationsAPI.getUnreadCount();
      const total = data.total || 0;
      setTotalUnreadCount(total);
      
      // Show banner if there are unread notifications
      if (total > 0) {
        setShowNotificationBanner(true);
      }
    } catch (error) {
      console.error('Error checking notifications:', error);
    }
  };

  // Handle tap on notification banner - plays sound and navigates
  const handleNotificationBannerTap = () => {
    notificationSound.init();
    notificationSound.playNotificationSound();
    setShowNotificationBanner(false);
    navigate('/notifications');
  };

  // Dismiss banner without navigation
  const dismissNotificationBanner = (e) => {
    e.stopPropagation();
    setShowNotificationBanner(false);
  };

  // Handle phone + PIN setup
  const handlePhoneSetup = async () => {
    setPhoneSetupError('');
    
    // Validate phone number (basic validation)
    const phoneDigits = phoneSetupData.phone.replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      setPhoneSetupError('Please enter a valid 10-digit phone number');
      return;
    }
    
    // Validate PIN
    if (!phoneSetupData.pin || phoneSetupData.pin.length < 4 || phoneSetupData.pin.length > 6) {
      setPhoneSetupError('PIN must be 4-6 digits');
      return;
    }
    
    if (!/^\d+$/.test(phoneSetupData.pin)) {
      setPhoneSetupError('PIN must contain only numbers');
      return;
    }
    
    if (phoneSetupData.pin !== phoneSetupData.confirmPin) {
      setPhoneSetupError('PINs do not match');
      return;
    }
    
    setPhoneSetupLoading(true);
    try {
      await authAPI.setupPhonePin(phoneSetupData.phone, phoneSetupData.pin);
      localStorage.setItem('phoneSetupCompleted', 'true');
      setShowPhoneSetupPrompt(false);
      alert('Phone and PIN set up successfully! You can now log in with your phone number.');
    } catch (error) {
      setPhoneSetupError(error.response?.data?.detail || 'Failed to set up phone login');
    } finally {
      setPhoneSetupLoading(false);
    }
  };

  const dismissPhoneSetup = () => {
    localStorage.setItem('phoneSetupDismissed', new Date().toISOString());
    setShowPhoneSetupPrompt(false);
  };

  // Load wishes for a celebration
  const loadCelebrationWishes = async (memberId) => {
    if (loadingWishes[memberId]) return;
    setLoadingWishes(prev => ({ ...prev, [memberId]: true }));
    try {
      const wishes = await celebrationsAPI.getWishes(memberId);
      setCelebrationWishes(prev => ({ ...prev, [memberId]: wishes }));
    } catch (error) {
      console.error('Error loading wishes:', error);
    } finally {
      setLoadingWishes(prev => ({ ...prev, [memberId]: false }));
    }
  };

  // Toggle expanded wishes view
  const toggleWishesExpanded = async (memberId) => {
    const isCurrentlyExpanded = expandedWishes[memberId];
    
    if (isCurrentlyExpanded) {
      // Collapsing - just collapse
      setExpandedWishes(prev => ({ ...prev, [memberId]: false }));
    } else {
      // Expanding - expand first, then load wishes
      setExpandedWishes(prev => ({ ...prev, [memberId]: true }));
      // Always reload wishes when expanding to get latest
      await loadCelebrationWishes(memberId);
    }
  };

  // Send celebration wish (Birthday or Anniversary)
  const sendCelebrationWish = async (memberId, memberName) => {
    const message = wishInputs[memberId]?.trim();
    if (!message) return;
    
    setSendingCelebrationMessage(memberId);
    try {
      const newWish = await celebrationsAPI.postWish(memberId, message);
      
      // Batch all state updates together to prevent re-render issues
      // First, ensure expanded state is preserved
      setExpandedWishes(prev => ({ ...prev, [memberId]: true }));
      
      // Update wishes list with new wish
      setCelebrationWishes(prev => ({
        ...prev,
        [memberId]: [...(prev[memberId] || []), newWish]
      }));
      
      // Clear input
      setWishInputs(prev => ({ ...prev, [memberId]: '' }));
      
      // Reload all wishes to ensure we have the latest from server
      // Use a longer delay and re-ensure expanded state after refresh
      setTimeout(async () => {
        try {
          const wishes = await celebrationsAPI.getWishes(memberId);
          setCelebrationWishes(prev => ({ ...prev, [memberId]: wishes }));
          // Re-ensure expanded state is still true after server refresh
          setExpandedWishes(prev => ({ ...prev, [memberId]: true }));
        } catch (e) {
          console.error('Error refreshing wishes:', e);
        }
      }, 800);
    } catch (error) {
      console.error('Error sending celebration wish:', error);
      alert('Failed to send wish. Please try again.');
    } finally {
      setSendingCelebrationMessage(null);
      // One more safety check - ensure expanded state persists
      setExpandedWishes(prev => ({ ...prev, [memberId]: true }));
    }
  };

  // Handle announcement reaction
  const handleAnnouncementReaction = async (announcementId, emoji) => {
    try {
      const result = await announcementsAPI.toggleReaction(announcementId, emoji);
      setAnnouncements(announcements.map(ann => {
        if (ann.id === announcementId) {
          return { ...ann, reactions: result.reactions };
        }
        return ann;
      }));
      // Clear cached reaction details so they get refreshed
      setReactionDetails(prev => ({ ...prev, [announcementId]: null }));
    } catch (error) {
      console.error('Error toggling reaction:', error);
    }
  };

  // Handle adding a comment to an announcement
  const handleAnnouncementComment = async (announcementId) => {
    const text = announcementCommentText[announcementId];
    if (!text?.trim()) return;
    
    try {
      const updatedAnnouncement = await announcementsAPI.addComment(announcementId, text.trim());
      setAnnouncements(announcements.map(ann => {
        if (ann.id === announcementId) {
          return { ...ann, comments: updatedAnnouncement.comments };
        }
        return ann;
      }));
      setAnnouncementCommentText({ ...announcementCommentText, [announcementId]: '' });
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  // Load reaction details (who reacted with each emoji)
  const loadReactionDetails = async (announcementId) => {
    if (loadingReactionDetails[announcementId]) return;
    
    setLoadingReactionDetails(prev => ({ ...prev, [announcementId]: true }));
    try {
      const data = await announcementsAPI.getReactionDetails(announcementId);
      setReactionDetails(prev => ({ ...prev, [announcementId]: data.reactions }));
    } catch (error) {
      console.error('Error loading reaction details:', error);
    } finally {
      setLoadingReactionDetails(prev => ({ ...prev, [announcementId]: false }));
    }
  };

  // Format date for comments
  const formatCommentDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const isYesterday = new Date(now - 86400000).toDateString() === date.toDateString();
    
    const timeStr = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    
    if (isToday) {
      return `Today at ${timeStr}`;
    } else if (isYesterday) {
      return `Yesterday at ${timeStr}`;
    } else {
      return date.toLocaleDateString([], { 
        month: 'short', 
        day: 'numeric'
      }) + ` at ${timeStr}`;
    }
  };

  const loadNewPrayerCount = async () => {
    try {
      const data = await prayersAPI.getNewCount();
      const newCount = data.newCount || 0;
      
      // Play sound if count increased (not on first load)
      if (!isFirstLoad.current && newCount > prevPrayerCount.current) {
        notificationSound.playPrayerSound();
      }
      prevPrayerCount.current = newCount;
      setNewPrayerCount(newCount);
    } catch (error) {
      console.error('Error loading prayer count:', error);
    }
  };

  const loadNewInspirationalCount = async () => {
    try {
      const data = await inspirationalAPI.getNewCount();
      setNewInspirationalCount(data.newCount || 0);
    } catch (error) {
      console.error('Error loading inspirational count:', error);
    }
  };

  const loadBibleDailyStatus = async () => {
    try {
      const data = await bibleAPI.getDailyStatus();
      setBibleHasNew(!!data.hasNew);
    } catch (error) {
      console.error('Error loading bible status:', error);
    }
  };

  const loadLivestreamStatus = async () => {
    try {
      const data = await livestreamAPI.getStatus();
      setIsLive(!!data.isLive);
    } catch (error) {
      console.error('Error loading livestream status:', error);
    }
  };

  const loadPendingBenevolentCount = async () => {
    try {
      const data = await benevolentAPI.getPendingCount();
      // Only show count if user is authorized
      if (data.authorized) {
        setPendingBenevolentCount(data.pendingCount || 0);
      }
    } catch (error) {
      console.error('Error loading benevolent count:', error);
    }
  };

  const loadMyBenevolentUpdatesCount = async () => {
    try {
      const data = await benevolentAPI.getMyUpdatesCount();
      const newCount = data.updatesCount || 0;
      
      // Play sound if count increased (not on first load)
      if (!isFirstLoad.current && newCount > prevBenevolentUpdates.current) {
        notificationSound.playBenevolentSound();
      }
      prevBenevolentUpdates.current = newCount;
      setMyBenevolentUpdatesCount(newCount);
    } catch (error) {
      console.error('Error loading benevolent updates count:', error);
    }
  };

  const loadNewAnnouncementCount = async () => {
    try {
      const data = await announcementsAPI.getNewCount();
      const newCount = data.newCount || 0;
      
      // Play sound if count increased (not on first load)
      if (!isFirstLoad.current && newCount > prevAnnouncementCount.current) {
        notificationSound.playAnnouncementSound();
      }
      prevAnnouncementCount.current = newCount;
      setNewAnnouncementCount(newCount);
      
      // Mark first load complete after all initial loads
      isFirstLoad.current = false;
    } catch (error) {
      console.error('Error loading announcement count:', error);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const isYesterday = new Date(now - 86400000).toDateString() === date.toDateString();
    
    const timeStr = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    
    if (isToday) {
      return `Today at ${timeStr}`;
    } else if (isYesterday) {
      return `Yesterday at ${timeStr}`;
    } else {
      return date.toLocaleDateString([], { 
        month: 'short', 
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      }) + ` at ${timeStr}`;
    }
  };

  const handleArchive = async (annId) => {
    try {
      await announcementsAPI.archive(annId);
      setAnnouncements(announcements.filter(a => a.id !== annId));
    } catch (error) {
      console.error('Error archiving announcement:', error);
    }
  };

  const handleDelete = async (annId) => {
    if (window.confirm('Are you sure you want to permanently delete this announcement? This action cannot be undone.')) {
      try {
        await announcementsAPI.delete(annId);
        setAnnouncements(announcements.filter(a => a.id !== annId));
      } catch (error) {
        console.error('Error deleting announcement:', error);
        alert('Failed to delete announcement. Please try again.');
      }
    }
  };

  const handleSendPastorMessage = async (e) => {
    e.preventDefault();
    setSendingMessage(true);
    try {
      await messagesAPI.send({
        recipientId: 'user-pastor',
        subject: pastorMessage.subject,
        content: pastorMessage.content
      });
      setMessageSent(true);
      setTimeout(() => {
        setShowPastorMessageDialog(false);
        setPastorMessage({ subject: '', content: '' });
        setMessageSent(false);
      }, 1500);
    } catch (error) {
      console.error('Error sending message to pastor:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSendingMessage(false);
    }
  };

  // Helper to get proper image URL
  const getImageUrl = (url) => {
    if (!url) return null;
    
    // Handle Cloudinary URLs (new cloud storage) - return as-is
    if (url.includes('cloudinary.com') || url.includes('res.cloudinary.com')) {
      return url;
    }
    
    // Handle old domains that no longer exist - these files are lost
    if (url.includes('smbc-connect.emergent.host') || url.includes('faith-hub')) {
      // Extract the path and try with current backend (file likely doesn't exist)
      try {
        const urlObj = new URL(url);
        const path = urlObj.pathname;
        return `${process.env.REACT_APP_BACKEND_URL}${path}`;
      } catch {
        return url;
      }
    }
    
    // Handle current emergentagent preview domains
    if (url.includes('.emergentagent.com') || url.includes('.preview.emergentagent.com')) {
      try {
        const urlObj = new URL(url);
        const path = urlObj.pathname;
        return `${process.env.REACT_APP_BACKEND_URL}${path}`;
      } catch {
        return url;
      }
    }
    
    // If it starts with http but not our domain, return as is
    if (url.startsWith('http')) return url;
    
    // Otherwise, prepend the backend URL
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-navy-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-tan-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* What's New Popup */}
      <WhatsNewPopup />
      
      {/* Notification Banner - shows when there are unread notifications */}
      {showNotificationBanner && totalUnreadCount > 0 && (
        <div 
          onClick={handleNotificationBannerTap}
          className="fixed top-0 left-0 right-0 bg-gradient-to-r from-tan-500 to-tan-600 text-white px-4 py-3 flex items-center justify-between z-50 shadow-lg cursor-pointer animate-pulse"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
              <Bell className="w-4 h-4" />
            </div>
            <div>
              <p className="font-semibold text-sm">You have {totalUnreadCount} new notification{totalUnreadCount > 1 ? 's' : ''}!</p>
              <p className="text-xs text-white/80 flex items-center gap-1">
                <Volume2 className="w-3 h-3" /> Tap to view
              </p>
            </div>
          </div>
          <button 
            onClick={dismissNotificationBanner}
            className="p-1 hover:bg-white/10 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
      
      {/* Header with gradient */}
      <div className={`bg-gradient-to-br from-navy-900 to-navy-800 text-white px-5 pt-8 pb-8 rounded-b-3xl shadow-xl ${showNotificationBanner && totalUnreadCount > 0 ? 'mt-14' : ''}`}>
        <p className="text-tan-400 text-sm mb-1 font-medium" data-testid="home-greeting">
          {(() => {
            const h = new Date().getHours();
            const part = h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
            return user?.firstName ? `${part}, ${user.firstName}!` : 'Welcome to';
          })()}
        </p>
        <h1 className="text-2xl font-bold mb-1">Second Missionary Baptist Church</h1>
        <p className="text-tan-400 text-sm italic">&ldquo;Where God gets ALL the Glory&rdquo;</p>
      </div>

      <div className="px-4 -mt-4 space-y-4">
        {/* Daily Devotional */}
        {devotional && (
          <Card className="border-0 shadow-lg rounded-2xl overflow-hidden">
            <CardContent className="p-5">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-tan-100 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-tan-600" />
                </div>
                <div>
                  <h3 className="font-bold text-navy-900">Daily Devotional</h3>
                  <p className="text-tan-600 font-semibold">{devotional.reference}</p>
                </div>
              </div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">King James Version</p>
              <blockquote className="text-gray-700 italic leading-relaxed">
                &ldquo;{devotional.verse}&rdquo;
              </blockquote>
              {devotional.reflection && (
                <p className="text-gray-600 text-sm mt-3 leading-relaxed">
                  {devotional.reflection}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Quick Links - Single Row */}
        <div className="grid grid-cols-5 gap-2">
          <button
            onClick={() => navigate('/bible')}
            className="flex flex-col items-center p-2 bg-white rounded-2xl shadow-md hover:shadow-lg transition-all"
          >
            <div className="w-10 h-10 rounded-full bg-tan-100 flex items-center justify-center mb-1 relative">
              <BookOpen className="w-5 h-5 text-tan-600" />
              {bibleHasNew && (
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-tan-500 rounded-full border-2 border-white" data-testid="bible-badge" title="Today's verse is ready" />
              )}
            </div>
            <span className="text-[10px] font-medium text-navy-900">Bible</span>
          </button>
          <button
            onClick={() => navigate('/prayer-wall')}
            className="flex flex-col items-center p-2 bg-white rounded-2xl shadow-md hover:shadow-lg transition-all relative"
          >
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mb-1 relative">
              {/* Praying Hands Icon */}
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-purple-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2C8.5 2 6 4.5 6 7c0 1.5.5 2.5 1.5 3.5L12 15l4.5-4.5C17.5 9.5 18 8.5 18 7c0-2.5-2.5-5-6-5Z"/>
                <path d="M12 15v7"/>
                <path d="M8 18h8"/>
                <path d="M9 12l3 3 3-3"/>
              </svg>
              {newPrayerCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-purple-500 rounded-full text-white text-[10px] flex items-center justify-center font-bold px-1">
                  {newPrayerCount > 9 ? '9+' : newPrayerCount}
                </span>
              )}
            </div>
            <span className="text-[10px] font-medium text-navy-900">Prayer</span>
          </button>
          <button
            onClick={() => navigate('/benevolent')}
            className="flex flex-col items-center p-2 bg-white rounded-2xl shadow-md hover:shadow-lg transition-all"
          >
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mb-1 relative">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>
                <path d="M12 5 9.04 7.96a2.17 2.17 0 0 0 0 3.08v0c.82.82 2.13.85 3 .07l2.07-1.9a2.82 2.82 0 0 1 3.79 0l2.96 2.66"/>
                <path d="m18 15-2-2"/>
                <path d="m15 18-2-2"/>
              </svg>
              {/* Show badge for admins (pending count) or members (updates count) */}
              {(pendingBenevolentCount > 0 || myBenevolentUpdatesCount > 0) && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-green-600 rounded-full text-white text-[10px] flex items-center justify-center font-bold px-1">
                  {(() => {
                    const total = pendingBenevolentCount + myBenevolentUpdatesCount;
                    return total > 9 ? '9+' : total;
                  })()}
                </span>
              )}
            </div>
            <span className="text-[10px] font-medium text-navy-900">Benevolent</span>
          </button>
          <button
            onClick={() => navigate('/inspirational')}
            className="flex flex-col items-center p-2 bg-white rounded-2xl shadow-md hover:shadow-lg transition-all"
            data-testid="inspirational-btn"
          >
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mb-1 relative">
              <Heart className="w-5 h-5 text-red-500" />
              {newInspirationalCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 rounded-full text-white text-[10px] flex items-center justify-center font-bold px-1" data-testid="inspire-badge">
                  {newInspirationalCount > 9 ? '9+' : newInspirationalCount}
                </span>
              )}
            </div>
            <span className="text-[10px] font-medium text-navy-900">Inspire</span>
          </button>
          <button
            onClick={() => setShowWatchDialog(true)}
            className="flex flex-col items-center p-2 bg-white rounded-2xl shadow-md hover:shadow-lg transition-all"
            data-testid="watch-btn"
          >
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mb-1 relative">
              <Youtube className="w-5 h-5 text-red-600" />
              {isLive && (
                <span className="absolute -top-1.5 -right-2 bg-red-600 rounded-full text-white text-[8px] font-bold px-1.5 py-0.5 flex items-center gap-0.5 animate-pulse" data-testid="watch-live-badge">
                  <span className="w-1.5 h-1.5 bg-white rounded-full inline-block" />
                  LIVE
                </span>
              )}
            </div>
            <span className="text-[10px] font-medium text-navy-900">Watch</span>
          </button>
        </div>

        {/* Church Directions */}
        <Card className="border-0 shadow-lg rounded-2xl overflow-hidden">
          <CardContent className="p-5">
            <div 
              className="flex items-start gap-3 cursor-pointer"
              onClick={() => window.open(churchInfo.mapUrl, '_blank')}
            >
              <div className="w-10 h-10 rounded-full bg-navy-900 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-tan-600 text-sm font-medium mb-1">Tap for directions</p>
                <h3 className="font-bold text-navy-900">Second Missionary Baptist Church</h3>
                <p className="text-gray-500 text-sm">{churchInfo.address}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-tan-500 mt-2" />
            </div>
            <div className="mt-3 pl-13 space-y-1">
              <a 
                href="tel:5596749321" 
                className="text-tan-600 text-sm font-medium flex items-center gap-2 hover:underline"
              >
                <Phone className="w-4 h-4" />
                (559) 674-9321
              </a>
              <a 
                href="https://www.smbcmadera.org" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-navy-700 text-sm flex items-center gap-2 hover:underline"
              >
                <Globe className="w-4 h-4" />
                www.smbcmadera.org
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Wednesday Bible Study - Zoom */}
        <Card className="border-0 shadow-lg rounded-2xl overflow-hidden bg-gradient-to-r from-navy-900 to-navy-800">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <Video className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="w-4 h-4 text-tan-400" />
                  <span className="text-tan-400 text-sm font-medium">Every Wednesday</span>
                </div>
                <h3 className="font-bold text-white text-lg">Bible Study</h3>
                <p className="text-gray-300 text-sm">6:00 PM via Zoom</p>
                
                <div className="mt-3 space-y-2">
                  <a
                    href="https://us02web.zoom.us/j/89578179522?pwd=elJ3VE5HbkE3V1F2eU9vUmQ0RDRSUT09"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full bg-tan-500 hover:bg-tan-600 text-white py-3 rounded-xl font-semibold transition-colors"
                  >
                    <Video className="w-5 h-5" />
                    Join on Zoom
                  </a>
                  
                  <div className="flex gap-2">
                    <a
                      href="tel:+16694449171,,89578179522#"
                      className="flex-1 flex items-center justify-center gap-1 bg-white/10 hover:bg-white/20 text-white py-2 rounded-xl text-sm transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                      One-Tap Call
                    </a>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText('895 7817 9522');
                        alert('Meeting ID copied!');
                      }}
                      className="flex-1 flex items-center justify-center gap-1 bg-white/10 hover:bg-white/20 text-white py-2 rounded-xl text-sm transition-colors"
                    >
                      <Copy className="w-4 h-4" />
                      Copy ID
                    </button>
                  </div>
                  
                  <div className="text-xs text-gray-400 text-center pt-1">
                    Meeting ID: 895 7817 9522 • Passcode: SecondB.C.
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tuesday Prayer Call - Zoom */}
        <Card className="border-0 shadow-lg rounded-2xl overflow-hidden bg-gradient-to-r from-navy-900 to-navy-800">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <Phone className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="w-4 h-4 text-tan-400" />
                  <span className="text-tan-400 text-sm font-medium">Every Tuesday</span>
                </div>
                <h3 className="font-bold text-white text-lg">Prayer Call</h3>
                <p className="text-gray-300 text-sm">6:00 PM via Zoom</p>
                
                <div className="mt-3 space-y-2">
                  <a
                    href="https://us02web.zoom.us/j/89578179522?pwd=elJ3VE5HbkE3V1F2eU9vUmQ0RDRSUT09"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full bg-tan-500 hover:bg-tan-600 text-white py-3 rounded-xl font-semibold transition-colors"
                    data-testid="prayer-call-zoom-btn"
                  >
                    <Video className="w-5 h-5" />
                    Join Prayer Call
                  </a>
                  
                  <div className="flex gap-2">
                    <a
                      href="tel:+16694449171,,89578179522#"
                      className="flex-1 flex items-center justify-center gap-1 bg-white/10 hover:bg-white/20 text-white py-2 rounded-xl text-sm transition-colors"
                      data-testid="prayer-call-one-tap"
                    >
                      <Phone className="w-4 h-4" />
                      One-Tap Call
                    </a>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText('895 7817 9522');
                        alert('Meeting ID copied!');
                      }}
                      className="flex-1 flex items-center justify-center gap-1 bg-white/10 hover:bg-white/20 text-white py-2 rounded-xl text-sm transition-colors"
                      data-testid="prayer-call-copy-id"
                    >
                      <Copy className="w-4 h-4" />
                      Copy ID
                    </button>
                  </div>
                  
                  <div className="text-xs text-gray-400 text-center pt-1">
                    Meeting ID: 895 7817 9522 • Passcode: SecondB.C.
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pastor Contact */}
        <Card className="border-0 shadow-lg rounded-2xl overflow-hidden">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-navy-100 flex items-center justify-center">
                <User className="w-7 h-7 text-navy-900" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-navy-900">Rev. Charles Bostick</h3>
                <p className="text-tan-600 text-sm font-medium">Pastor</p>
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <a 
                href="tel:5597188081"
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-navy-900 text-white rounded-xl hover:bg-navy-800 transition-colors"
              >
                <Phone className="w-4 h-4" />
                <span className="font-medium">Call</span>
              </a>
              <button 
                onClick={() => setShowPastorMessageDialog(true)}
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-tan-500 text-white rounded-xl hover:bg-tan-600 transition-colors"
              >
                <MessageSquare className="w-4 h-4" />
                <span className="font-medium">Message</span>
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Service Times */}
        <Card className="border-0 shadow-lg rounded-2xl overflow-hidden">
          <CardContent className="p-5">
            <h3 className="font-bold text-navy-900 mb-3">Service Times</h3>
            <div className="space-y-2">
              {churchInfo.services.map((service, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <span className="text-navy-900 font-medium">{service.name}</span>
                  <span className="text-tan-600 font-medium">{service.day} {service.time}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Today's Celebrations */}
        {celebrations.hasCelebrations && (
          <Card className="border-0 shadow-lg rounded-2xl overflow-hidden bg-gradient-to-r from-pink-50 to-amber-50" data-testid="celebrations-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <PartyPopper className="w-5 h-5 text-pink-500" />
                <h3 className="font-bold text-navy-900">Today's Celebrations</h3>
              </div>
              
              <div className="space-y-4">
                {/* Birthdays */}
                {celebrations.birthdays.length > 0 && (
                  <div className="space-y-3">
                    {celebrations.birthdays.map((member) => (
                      <div key={`birthday-${member.id}`} className="bg-white rounded-xl shadow-sm overflow-hidden">
                        <div className="flex items-center justify-between p-3">
                          <div className="flex items-center gap-3">
                            <div className="relative">
                              {member.avatar ? (
                                <img 
                                  src={member.avatar.startsWith('http') ? member.avatar : `${process.env.REACT_APP_BACKEND_URL}${member.avatar}`} 
                                  alt={member.name}
                                  className="w-12 h-12 rounded-full object-cover border-2 border-pink-300"
                                />
                              ) : (
                                <div className="w-12 h-12 rounded-full bg-pink-200 flex items-center justify-center text-pink-700 font-semibold border-2 border-pink-300">
                                  {member.name.split(' ').map(n => n[0]).join('')}
                                </div>
                              )}
                              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-pink-500 rounded-full flex items-center justify-center">
                                <Cake className="w-3.5 h-3.5 text-white" />
                              </div>
                            </div>
                            <div>
                              <p className="font-semibold text-navy-900">{member.name}</p>
                              <p className="text-sm text-pink-600 font-medium">Happy Birthday! 🎂</p>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-pink-600"
                            onClick={() => toggleWishesExpanded(member.id)}
                            data-testid={`toggle-wishes-${member.id}`}
                          >
                            <MessageSquare className="w-4 h-4 mr-1" />
                            {celebrationWishes[member.id]?.length || 0}
                          </Button>
                        </div>
                        
                        {/* Wishes Section */}
                        {expandedWishes[member.id] && (
                          <div className="border-t border-pink-100 p-3 bg-pink-50/50">
                            {/* Existing Wishes */}
                            {celebrationWishes[member.id]?.length > 0 && (
                              <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
                                {celebrationWishes[member.id].map((wish) => (
                                  <div key={wish.id} className="flex gap-2 p-2 bg-white rounded-lg">
                                    {wish.senderAvatar ? (
                                      <img 
                                        src={wish.senderAvatar.startsWith('http') ? wish.senderAvatar : `${process.env.REACT_APP_BACKEND_URL}${wish.senderAvatar}`}
                                        alt=""
                                        className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                                      />
                                    ) : (
                                      <div className="w-8 h-8 rounded-full bg-pink-200 flex items-center justify-center text-pink-700 text-xs font-semibold flex-shrink-0">
                                        {wish.senderName.split(' ').map(n => n[0]).join('')}
                                      </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs font-semibold text-navy-900">{wish.senderName}</p>
                                      <p className="text-sm text-gray-700 break-words">{wish.message}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {loadingWishes[member.id] && (
                              <div className="flex justify-center py-2">
                                <Loader2 className="w-5 h-5 animate-spin text-pink-500" />
                              </div>
                            )}
                            
                            {/* Write a Wish */}
                            {member.id !== user?.id && (
                              <div className="space-y-2">
                                <p className="text-xs font-medium text-pink-600 flex items-center gap-1">
                                  <Gift className="w-3 h-3" />
                                  Leave a birthday wish for {member.name.split(' ')[0]}!
                                </p>
                                <div className="flex gap-2 items-end">
                                  <div className="flex-1 relative">
                                    <textarea
                                      placeholder={`Type your message here... 🎂🎉🎁`}
                                      value={wishInputs[member.id] || ''}
                                      onChange={(e) => setWishInputs(prev => ({ ...prev, [member.id]: e.target.value }))}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                          e.preventDefault();
                                          if (wishInputs[member.id]?.trim()) {
                                            sendCelebrationWish(member.id, member.name);
                                          }
                                        }
                                      }}
                                      className="w-full p-2 pr-8 text-sm border border-pink-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-pink-300 min-h-[40px] max-h-[100px]"
                                      rows={1}
                                      onInput={(e) => {
                                        e.target.style.height = 'auto';
                                        e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
                                      }}
                                    />
                                  </div>
                                  <Button
                                    size="sm"
                                    className="bg-pink-500 hover:bg-pink-600 text-white"
                                    onClick={() => sendCelebrationWish(member.id, member.name)}
                                    disabled={!wishInputs[member.id]?.trim() || sendingCelebrationMessage === member.id}
                                  >
                                    {sendingCelebrationMessage === member.id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <Send className="w-4 h-4" />
                                    )}
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Anniversaries */}
                {celebrations.anniversaries.length > 0 && (
                  <div className="space-y-3">
                    {celebrations.anniversaries.map((member) => (
                      <div key={`anniversary-${member.id}`} className="bg-white rounded-xl shadow-sm overflow-hidden">
                        <div className="flex items-center justify-between p-3">
                          <div className="flex items-center gap-3">
                            <div className="relative">
                              {member.avatar ? (
                                <img 
                                  src={member.avatar.startsWith('http') ? member.avatar : `${process.env.REACT_APP_BACKEND_URL}${member.avatar}`} 
                                  alt={member.name}
                                  className="w-12 h-12 rounded-full object-cover border-2 border-red-300"
                                />
                              ) : (
                                <div className="w-12 h-12 rounded-full bg-red-200 flex items-center justify-center text-red-700 font-semibold border-2 border-red-300">
                                  {member.name.split(' ').map(n => n[0]).join('')}
                                </div>
                              )}
                              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                                <Heart className="w-3.5 h-3.5 text-white" />
                              </div>
                            </div>
                            <div>
                              <p className="font-semibold text-navy-900">{member.name}</p>
                              <p className="text-sm text-red-600 font-medium">
                                Happy {member.years > 0 ? `${member.years}${member.years === 1 ? 'st' : member.years === 2 ? 'nd' : member.years === 3 ? 'rd' : 'th'} ` : ''}Anniversary! 💍
                              </p>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600"
                            onClick={() => toggleWishesExpanded(member.id)}
                            data-testid={`toggle-anniversary-wishes-${member.id}`}
                          >
                            <MessageSquare className="w-4 h-4 mr-1" />
                            {celebrationWishes[member.id]?.length || 0}
                          </Button>
                        </div>
                        
                        {/* Wishes Section */}
                        {expandedWishes[member.id] && (
                          <div className="border-t border-red-100 p-3 bg-red-50/50">
                            {/* Existing Wishes */}
                            {celebrationWishes[member.id]?.length > 0 && (
                              <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
                                {celebrationWishes[member.id].map((wish) => (
                                  <div key={wish.id} className="flex gap-2 p-2 bg-white rounded-lg">
                                    {wish.senderAvatar ? (
                                      <img 
                                        src={wish.senderAvatar.startsWith('http') ? wish.senderAvatar : `${process.env.REACT_APP_BACKEND_URL}${wish.senderAvatar}`}
                                        alt=""
                                        className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                                      />
                                    ) : (
                                      <div className="w-8 h-8 rounded-full bg-red-200 flex items-center justify-center text-red-700 text-xs font-semibold flex-shrink-0">
                                        {wish.senderName.split(' ').map(n => n[0]).join('')}
                                      </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs font-semibold text-navy-900">{wish.senderName}</p>
                                      <p className="text-sm text-gray-700 break-words">{wish.message}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {loadingWishes[member.id] && (
                              <div className="flex justify-center py-2">
                                <Loader2 className="w-5 h-5 animate-spin text-red-500" />
                              </div>
                            )}
                            
                            {/* Write a Wish */}
                            {member.id !== user?.id && (
                              <div className="space-y-2">
                                <p className="text-xs font-medium text-red-600 flex items-center gap-1">
                                  <Heart className="w-3 h-3" />
                                  Leave an anniversary wish for {member.name.split(' ')[0]}!
                                </p>
                                <div className="flex gap-2 items-end">
                                  <div className="flex-1 relative">
                                    <textarea
                                      placeholder={`Type your message here... 💍🎉❤️`}
                                      value={wishInputs[member.id] || ''}
                                      onChange={(e) => setWishInputs(prev => ({ ...prev, [member.id]: e.target.value }))}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                          e.preventDefault();
                                          if (wishInputs[member.id]?.trim()) {
                                            sendCelebrationWish(member.id, member.name);
                                          }
                                        }
                                      }}
                                      className="w-full p-2 pr-8 text-sm border border-red-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-red-300 min-h-[40px] max-h-[100px]"
                                      rows={1}
                                      onInput={(e) => {
                                        e.target.style.height = 'auto';
                                        e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
                                      }}
                                    />
                                  </div>
                                  <Button
                                    size="sm"
                                    className="bg-red-500 hover:bg-red-600 text-white"
                                    onClick={() => sendCelebrationWish(member.id, member.name)}
                                    disabled={!wishInputs[member.id]?.trim() || sendingCelebrationMessage === member.id}
                                  >
                                    {sendingCelebrationMessage === member.id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <Send className="w-4 h-4" />
                                    )}
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Announcements */}
        <Card id="announcements-section" className="border-0 shadow-lg rounded-2xl overflow-hidden">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 relative">
                <div className="relative">
                  <Bell className="w-5 h-5 text-tan-600" />
                  {newAnnouncementCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] bg-red-500 rounded-full text-white text-[8px] flex items-center justify-center font-bold">
                      {newAnnouncementCount > 9 ? '9+' : newAnnouncementCount}
                    </span>
                  )}
                </div>
                <h3 className="font-bold text-navy-900">Announcements</h3>
                {newAnnouncementCount > 0 && (
                  <span className="text-xs text-red-500 font-medium">New!</span>
                )}
              </div>
              {announcements.length > 0 && (
                <Badge className="bg-navy-900 text-white">{announcements.length}</Badge>
              )}
            </div>
            {announcements.length > 0 ? (
              <div className="space-y-4">
                {announcements.slice(0, 5).map((ann) => (
                  <div key={ann.id} className="p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {ann.priority === 'high' && (
                            <Badge className="bg-tan-500 text-white text-xs">Important</Badge>
                          )}
                          <span className="text-xs text-gray-400">{formatDate(ann.createdAt)}</span>
                        </div>
                        <h4 className="font-medium text-navy-900 text-sm">{ann.title}</h4>
                        <p className="text-xs text-gray-600 mt-1 whitespace-pre-wrap">
                          <LinkifyText>{ann.content}</LinkifyText>
                        </p>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                            <MoreVertical className="w-4 h-4 text-gray-400" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleArchive(ann.id)}>
                            <Archive className="w-4 h-4 mr-2" />
                            Archive
                          </DropdownMenuItem>
                          {(user?.role === 'pastor' || user?.role === 'admin') && (
                            <DropdownMenuItem 
                              onClick={() => handleDelete(ann.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    
                    {/* Announcement Image/Document - Clickable to view full size */}
                    {ann.imageUrl && (
                      <div className="mt-3">
                        {ann.imageUrl.toLowerCase().endsWith('.pdf') ? (
                          // PDF Document - show download/view link
                          <a
                            href={getImageUrl(ann.imageUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 bg-navy-100 hover:bg-navy-200 rounded-lg transition-colors"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-navy-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                            <span className="text-sm font-medium text-navy-700">View Document (PDF)</span>
                          </a>
                        ) : (
                          // Image - thumbnail with click to enlarge
                          <div 
                            className="relative cursor-pointer group inline-block"
                            onClick={() => setSelectedImage(getImageUrl(ann.imageUrl))}
                          >
                            <img 
                              src={getImageUrl(ann.imageUrl)} 
                              alt={ann.title}
                              className="max-w-full max-h-48 rounded-lg object-contain border border-gray-200"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded-lg flex items-center justify-center">
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-2">
                                <ZoomIn className="w-5 h-5 text-navy-900" />
                              </div>
                            </div>
                            <p className="text-xs text-tan-600 mt-1 font-medium">Tap image to enlarge</p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Announcement Video */}
                    {ann.videoUrl && (
                      <div className="mt-3">
                        <video 
                          src={getImageUrl(ann.videoUrl)} 
                          controls 
                          className="w-full rounded-lg max-h-48"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        >
                          <p className="text-sm text-gray-500">Video unavailable</p>
                        </video>
                      </div>
                    )}
                    
                    {/* Reactions and Comments Section */}
                    <div className="mt-3 pt-2 border-t border-gray-100">
                      {/* Reactions Row */}
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Existing Reactions with hover to show names */}
                        {ann.reactions && Object.keys(ann.reactions).length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(ann.reactions).map(([emoji, users]) => (
                              <Popover key={emoji}>
                                <PopoverTrigger asChild>
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      loadReactionDetails(ann.id);
                                    }}
                                    onDoubleClick={() => handleAnnouncementReaction(ann.id, emoji)}
                                    className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-colors ${
                                      users.includes(user?.id)
                                        ? 'bg-tan-200 text-tan-800 border border-tan-300'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                    title="Tap to see who reacted, double-tap to toggle"
                                  >
                                    <span>{emoji}</span>
                                    <span>{users.length}</span>
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-48 p-2" side="top">
                                  <div className="text-xs">
                                    <p className="font-semibold mb-1">{emoji} Reactions</p>
                                    {loadingReactionDetails[ann.id] ? (
                                      <p className="text-gray-400">Loading...</p>
                                    ) : reactionDetails[ann.id]?.[emoji] ? (
                                      <div className="space-y-1 max-h-32 overflow-y-auto">
                                        {reactionDetails[ann.id][emoji].map((reactor) => (
                                          <p key={reactor.id} className="text-gray-600">{reactor.name}</p>
                                        ))}
                                      </div>
                                    ) : (
                                      <p className="text-gray-400">Tap to load names</p>
                                    )}
                                    <button
                                      onClick={() => handleAnnouncementReaction(ann.id, emoji)}
                                      className="mt-2 w-full text-center py-1 bg-gray-100 hover:bg-gray-200 rounded text-xs"
                                    >
                                      {users.includes(user?.id) ? 'Remove your reaction' : 'Add your reaction'}
                                    </button>
                                  </div>
                                </PopoverContent>
                              </Popover>
                            ))}
                          </div>
                        )}
                        
                        {/* Add Reaction Button */}
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              className="p-1.5 text-gray-400 hover:text-tan-600 hover:bg-gray-100 rounded-full transition-colors"
                              title="Add reaction"
                            >
                              <SmilePlus className="w-4 h-4" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-2" side="top">
                            <div className="flex gap-1">
                              {REACTION_EMOJIS.map((emoji) => (
                                <button
                                  key={emoji}
                                  onClick={() => handleAnnouncementReaction(ann.id, emoji)}
                                  className="text-xl hover:scale-125 transition-transform p-1"
                                >
                                  {emoji}
                                </button>
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                        
                        {/* Comments Toggle Button */}
                        <button
                          onClick={() => setExpandedAnnouncementComments(prev => ({
                            ...prev,
                            [ann.id]: !prev[ann.id]
                          }))}
                          className="flex items-center gap-1 px-2 py-1 text-gray-500 hover:text-tan-600 hover:bg-gray-100 rounded-full text-xs transition-colors ml-auto"
                        >
                          <MessageSquare className="w-4 h-4" />
                          <span>{ann.comments?.length || 0} {ann.comments?.length === 1 ? 'Response' : 'Responses'}</span>
                        </button>
                      </div>
                      
                      {/* Comments Section - Expandable */}
                      {expandedAnnouncementComments[ann.id] && (
                        <div className="mt-3 space-y-2">
                          {/* Existing Comments */}
                          {ann.comments?.length > 0 && (
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                              {ann.comments.map((comment) => (
                                <div key={comment.id} className="bg-gray-50 rounded-xl p-3">
                                  <div className="flex justify-between items-center mb-1">
                                    <span className="text-sm font-semibold text-navy-900">{comment.userName}</span>
                                    <span className="text-xs text-gray-400">{formatCommentDate(comment.createdAt)}</span>
                                  </div>
                                  <p className="text-sm text-gray-600">{comment.text}</p>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* Add Comment Input */}
                          <div className="flex gap-2 mt-2 items-end">
                            <textarea
                              placeholder="Write a response..."
                              value={announcementCommentText[ann.id] || ''}
                              onChange={(e) => {
                                setAnnouncementCommentText({
                                  ...announcementCommentText,
                                  [ann.id]: e.target.value
                                });
                                // Auto-resize textarea
                                e.target.style.height = 'auto';
                                e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  handleAnnouncementComment(ann.id);
                                }
                              }}
                              className="flex-1 text-sm px-3 py-2 border border-gray-200 rounded-xl resize-none overflow-hidden focus:outline-none focus:ring-2 focus:ring-tan-500 focus:border-transparent"
                              style={{ minHeight: '40px', maxHeight: '100px' }}
                              rows={1}
                            />
                            <Button
                              size="sm"
                              onClick={() => handleAnnouncementComment(ann.id)}
                              disabled={!announcementCommentText[ann.id]?.trim()}
                              className="bg-tan-600 hover:bg-tan-700 flex-shrink-0"
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-gray-500">
                <Bell className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No announcements at this time</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Image Lightbox Dialog */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl w-[95vw] p-0 bg-transparent border-0 shadow-none">
          <div className="relative">
            {/* Close button */}
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors z-50"
            >
              <X className="w-6 h-6 text-gray-800" />
            </button>
            
            {/* Full size image */}
            {selectedImage && (
              <img 
                src={selectedImage} 
                alt="Announcement"
                className="w-full h-auto max-h-[85vh] object-contain rounded-lg bg-white"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Pastor Message Dialog */}
      <Dialog open={showPastorMessageDialog} onOpenChange={setShowPastorMessageDialog}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-navy-900">Message Pastor Bostick</DialogTitle>
          </DialogHeader>
          {messageSent ? (
            <div className="py-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Send className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-lg font-medium text-navy-900">Message Sent!</p>
              <p className="text-sm text-gray-500 mt-1">Pastor Bostick will receive your message.</p>
            </div>
          ) : (
            <form onSubmit={handleSendPastorMessage} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Subject</label>
                <Input
                  placeholder="What is this about?"
                  value={pastorMessage.subject}
                  onChange={(e) => setPastorMessage({ ...pastorMessage, subject: e.target.value })}
                  className="mt-1 h-11 bg-gray-50 border-gray-200 rounded-xl"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Message</label>
                <textarea
                  placeholder="Write your message to Pastor Bostick..."
                  value={pastorMessage.content}
                  onChange={(e) => setPastorMessage({ ...pastorMessage, content: e.target.value })}
                  className="mt-1 w-full h-32 p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-tan-500"
                  required
                />
              </div>
              <Button
                type="submit"
                className="w-full h-11 bg-tan-500 hover:bg-tan-600 text-white rounded-xl font-medium"
                disabled={sendingMessage}
              >
                {sendingMessage ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send Message
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Watch Dialog - YouTube & Facebook */}
      <Dialog open={showWatchDialog} onOpenChange={setShowWatchDialog}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-navy-900">Watch Our Services</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-gray-600 mb-4">
              Watch our live services and past recordings on your preferred platform.
            </p>
            
            {/* YouTube Button */}
            <button
              onClick={() => {
                window.open('https://www.youtube.com/@secondmissionarybaptistmadera', '_blank');
                setShowWatchDialog(false);
              }}
              className="w-full flex items-center gap-4 p-4 bg-red-50 hover:bg-red-100 rounded-xl transition-colors"
              data-testid="watch-youtube-btn"
            >
              <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center">
                <Youtube className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 text-left">
                <h4 className="font-bold text-red-900">YouTube</h4>
                <p className="text-sm text-red-700">Watch on our YouTube channel</p>
              </div>
              <ChevronRight className="w-5 h-5 text-red-400" />
            </button>
            
            {/* Facebook Button */}
            <button
              onClick={() => {
                window.open('https://www.facebook.com/profile.php?id=100077476542701', '_blank');
                setShowWatchDialog(false);
              }}
              className="w-full flex items-center gap-4 p-4 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors"
              data-testid="watch-facebook-btn"
            >
              <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center">
                <Facebook className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 text-left">
                <h4 className="font-bold text-blue-900">Facebook</h4>
                <p className="text-sm text-blue-700">Watch on our Facebook page</p>
              </div>
              <ChevronRight className="w-5 h-5 text-blue-400" />
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Push Notification Prompt Dialog */}
      <Dialog open={showPushPrompt} onOpenChange={setShowPushPrompt}>
        <DialogContent className="sm:max-w-md rounded-2xl" data-testid="push-prompt-dialog">
          {isIOSDevice() && !isStandalonePWA() ? (
            <>
              <DialogHeader>
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-navy-100 flex items-center justify-center">
                    <Smartphone className="w-8 h-8 text-navy-600" />
                  </div>
                </div>
                <DialogTitle className="text-center text-xl text-navy-900">
                  Turn on notifications on your iPhone
                </DialogTitle>
                <DialogDescription className="text-center text-gray-600 mt-2">
                  Apple only allows notifications after you add this app to your Home Screen. It takes 10 seconds:
                </DialogDescription>
              </DialogHeader>
              <div className="mt-4 space-y-3" data-testid="ios-install-steps">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 h-7 w-7 flex items-center justify-center rounded-full bg-tan-100 text-tan-700 font-bold text-sm">1</span>
                  <p className="text-navy-900 text-base pt-0.5">
                    Tap the <Share className="inline w-4 h-4 mx-1 -mt-1 text-navy-700" /> <span className="font-semibold">Share</span> button at the bottom of Safari.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 h-7 w-7 flex items-center justify-center rounded-full bg-tan-100 text-tan-700 font-bold text-sm">2</span>
                  <p className="text-navy-900 text-base pt-0.5">
                    Scroll down and tap <PlusSquare className="inline w-4 h-4 mx-1 -mt-1 text-navy-700" /> <span className="font-semibold">"Add to Home Screen"</span>.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 h-7 w-7 flex items-center justify-center rounded-full bg-tan-100 text-tan-700 font-bold text-sm">3</span>
                  <p className="text-navy-900 text-base pt-0.5">
                    Open the app from your Home Screen, then tap <span className="font-semibold">"Enable Notifications"</span> here.
                  </p>
                </div>
              </div>
              <DialogFooter className="mt-5">
                <Button
                  variant="ghost"
                  onClick={handleDismissPushPrompt}
                  className="w-full text-gray-500 hover:text-gray-700"
                  data-testid="push-prompt-ios-dismiss"
                >
                  Got it
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-tan-100 flex items-center justify-center">
                    <BellRing className="w-8 h-8 text-tan-600" />
                  </div>
                </div>
                <DialogTitle className="text-center text-xl text-navy-900">
                  Stay Connected!
                </DialogTitle>
                <DialogDescription className="text-center text-gray-600 mt-2">
                  Enable notifications to receive alerts for new messages, prayer requests, announcements, and more - even when the app is closed.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="flex-col gap-2 sm:flex-col mt-4">
                <Button
                  onClick={handleEnablePush}
                  disabled={pushPromptLoading}
                  className="w-full h-12 bg-tan-500 hover:bg-tan-600 text-white rounded-xl font-semibold"
                  data-testid="push-prompt-enable-btn"
                >
                  {pushPromptLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <Bell className="w-5 h-5 mr-2" />
                  )}
                  Enable Notifications
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleDismissPushPrompt}
                  className="w-full text-gray-500 hover:text-gray-700"
                  data-testid="push-prompt-later-btn"
                >
                  Maybe Later
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Phone + PIN Setup Dialog */}
      <Dialog open={showPhoneSetupPrompt} onOpenChange={setShowPhoneSetupPrompt}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-navy-100 flex items-center justify-center">
                <Phone className="w-8 h-8 text-navy-600" />
              </div>
            </div>
            <DialogTitle className="text-center text-xl text-navy-900">
              Set Up Quick Login
            </DialogTitle>
            <DialogDescription className="text-center text-gray-600 mt-2">
              Add your phone number and create a PIN for faster, easier login. You can still use your email if you prefer.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {phoneSetupError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm text-center">
                {phoneSetupError}
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Phone Number</label>
              <Input
                type="tel"
                placeholder="(555) 123-4567"
                value={phoneSetupData.phone}
                onChange={(e) => setPhoneSetupData({ ...phoneSetupData, phone: e.target.value })}
                className="h-12 rounded-xl"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Create PIN (4-6 digits)</label>
              <Input
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="Enter 4-6 digit PIN"
                value={phoneSetupData.pin}
                onChange={(e) => setPhoneSetupData({ ...phoneSetupData, pin: e.target.value.replace(/\D/g, '') })}
                className="h-12 rounded-xl"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Confirm PIN</label>
              <Input
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="Re-enter your PIN"
                value={phoneSetupData.confirmPin}
                onChange={(e) => setPhoneSetupData({ ...phoneSetupData, confirmPin: e.target.value.replace(/\D/g, '') })}
                className="h-12 rounded-xl"
              />
            </div>
          </div>
          
          <DialogFooter className="flex-col gap-2 sm:flex-col mt-6">
            <Button
              onClick={handlePhoneSetup}
              disabled={phoneSetupLoading}
              className="w-full h-12 bg-navy-900 hover:bg-navy-800 text-white rounded-xl font-semibold"
            >
              {phoneSetupLoading ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <Check className="w-5 h-5 mr-2" />
              )}
              Set Up Phone Login
            </Button>
            <Button
              variant="ghost"
              onClick={dismissPhoneSetup}
              className="w-full text-gray-500 hover:text-gray-700"
            >
              Remind Me Later
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
};

export default HomePage;
